import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, CheckCircle2, AlertTriangle, Loader2, ExternalLink } from 'lucide-react';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/hooks/useToast';
import * as settingsApi from '@/api/settings.api';

/**
 * LA CLAVE DE IA — LO ÚNICO QUE ATHENA NECESITA PARA PENSAR.
 *
 * ═══ POR QUÉ EXISTE ESTA PANTALLA ═══
 *
 * La pantalla para guardar la clave ya existía, dentro de `/settings`, pero no
 * estaba en ningún menú. El 21-sep-2026 le dije a Esteban "Ajustes → Clave de
 * IA", y él, mirando su propio panel, contestó: *"mi plataforma no tiene ese
 * flujo"*. Tenía razón. Una función a la que no se puede llegar no existe.
 *
 * Y `/settings` es la pantalla del producto viejo: horarios del agente,
 * catálogo, avisos de técnicos. Mandarlo ahí era mandarlo a buscar una cosa
 * entre veinte que no son para él.
 *
 * ═══ GUARDAR Y COMPROBAR SON UN SOLO PASO ═══
 *
 * Al guardar, se prueba enseguida contra Anthropic (una llamada de un token).
 * Una clave mal copiada —un espacio de más, un carácter comido— se guarda
 * perfecta y falla en silencio a la mañana siguiente, cuando el inspector
 * intenta leer y no puede. Decirlo en el momento cuesta un centavo.
 *
 * ═══ LA CLAVE NO VUELVE ═══
 *
 * El servidor la guarda cifrada y nunca la devuelve: acá solo se ve el final
 * enmascarado. El campo es de contraseña y sin autocompletar, para que no quede
 * en el historial del navegador.
 */

export default function ClaveDeIA() {
  const qc = useQueryClient();
  const toast = useToast();
  const [clave, setClave] = useState('');
  const [resultado, setResultado] = useState<'ok' | 'mala' | null>(null);

  const claves = useQuery({
    queryKey: ['settings', 'api-keys', 'anthropic'],
    queryFn: () => settingsApi.getApiKeys('anthropic'),
  });

  const actual = claves.data?.[0] ?? null;

  const probar = useMutation({
    mutationFn: (id: string) => settingsApi.testApiKey(id),
    onSuccess: (r) => setResultado(r.valid ? 'ok' : 'mala'),
    onError: () => setResultado('mala'),
  });

  const guardar = useMutation({
    mutationFn: () => settingsApi.saveApiKey({ provider: 'anthropic', key: clave.trim() }),
    onSuccess: (guardada) => {
      setClave('');
      setResultado(null);
      void qc.invalidateQueries({ queryKey: ['settings', 'api-keys'] });
      // Se prueba enseguida: ver el paso de arriba.
      probar.mutate(guardada.id);
    },
    onError: (e: Error) => toast.error(e.message || 'No se pudo guardar la clave'),
  });

  const quitar = useMutation({
    mutationFn: (id: string) => settingsApi.deleteApiKey(id),
    onSuccess: () => {
      setResultado(null);
      void qc.invalidateQueries({ queryKey: ['settings', 'api-keys'] });
      toast.info('Clave quitada. Athena deja de leer hasta que pongas otra.');
    },
  });

  // Una clave de Anthropic empieza así. No es una validación fuerte —eso lo
  // hace la prueba contra el servidor—, es para atajar el error más común:
  // pegar otra cosa del portapapeles.
  const pareceClave = clave.trim().startsWith('sk-ant-');

  return (
    <div className="max-w-xl space-y-3">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-50">Clave de IA</h1>
        <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-300">
          Es lo que Athena necesita para leer tus chats y pensar.
        </p>
      </div>

      {/* ── EL ESTADO, PRIMERO ── */}
      <Card>
        <CardBody className="space-y-2">
          {claves.isPending ? (
            <p className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Mirando…
            </p>
          ) : actual ? (
            <>
              <p className="flex items-center gap-2 text-sm font-medium text-slate-800 dark:text-slate-100">
                <KeyRound className="h-4 w-4 text-brand-600" />
                Clave guardada
                <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs dark:bg-slate-800">
                  {actual.masked}
                </code>
              </p>

              {probar.isPending && (
                <p className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" /> Pruebandola con Anthropic…
                </p>
              )}
              {resultado === 'ok' && (
                <p className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-300">
                  <CheckCircle2 className="h-4 w-4" /> Funciona. Athena empieza a leer en su próxima
                  pasada (cada media hora).
                </p>
              )}
              {resultado === 'mala' && (
                <p className="flex items-start gap-2 rounded bg-amber-50 p-2 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  Anthropic la rechazó. Lo más común: se copió incompleta, o la cuenta no tiene
                  saldo. Crea otra y pégala abajo.
                </p>
              )}

              <div className="flex flex-wrap gap-2 pt-1">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={probar.isPending}
                  onClick={() => probar.mutate(actual.id)}
                >
                  Probarla
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={quitar.isPending}
                  onClick={() => quitar.mutate(actual.id)}
                >
                  Quitarla
                </Button>
              </div>
            </>
          ) : (
            <p className="flex items-start gap-2 text-sm text-amber-800 dark:text-amber-200">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              No hay clave. Sin ella Athena no lee tus chats, no te avisa de lo personal y no te
              contesta.
            </p>
          )}
        </CardBody>
      </Card>

      {/* ── PONER UNA (O CAMBIARLA) ── */}
      <Card>
        <CardBody className="space-y-3">
          <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
            {actual ? 'Cambiarla por otra' : 'Poner la clave'}
          </p>

          <ol className="list-inside list-decimal space-y-1 text-sm text-slate-600 dark:text-slate-300">
            <li>
              Entra a{' '}
              <a
                href="https://console.anthropic.com/settings/keys"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-0.5 text-brand-600 underline-offset-2 hover:underline dark:text-brand-300"
              >
                console.anthropic.com <ExternalLink className="h-3 w-3" />
              </a>
            </li>
            <li>Crea una clave nueva y cópiala</li>
            <li>Pégala aquí. Se guarda cifrada y no se vuelve a mostrar</li>
          </ol>

          <input
            type="password"
            autoComplete="off"
            spellCheck={false}
            value={clave}
            onChange={(e) => setClave(e.target.value)}
            placeholder="sk-ant-…"
            className="min-h-[44px] w-full rounded-lg border border-slate-200 px-3 font-mono text-sm dark:border-slate-700 dark:bg-slate-900"
          />

          {clave.trim() && !pareceClave && (
            <p className="text-xs text-amber-700 dark:text-amber-300">
              Eso no parece una clave de Anthropic: empiezan por «sk-ant-».
            </p>
          )}

          <Button
            className="w-full"
            disabled={!pareceClave || guardar.isPending}
            onClick={() => guardar.mutate()}
          >
            {guardar.isPending ? 'Guardando…' : 'Guardar y probar'}
          </Button>

          {actual && (
            <p className="text-xs text-slate-500">
              La anterior deja de usarse en el mismo momento. Bórrala también en Anthropic.
            </p>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
